import { createContext, useContext, type ComponentType } from "react"
import { useAuth } from "@app/hooks"
import { App } from "@app/interfaces"
import { hasAccessPolicy, hasModuleAccess } from "@app/utils"
import { postBotSessionIdentity } from "../../utils/sessionIdentity"

export interface PostBotAccess {
  canRead: boolean
  canWrite: boolean
  readOnly: boolean
  canAccessRetained: boolean
}
const denied: PostBotAccess = {
  canRead: false,
  canWrite: false,
  readOnly: true,
  canAccessRetained: false,
}
export const PostBotAccessContext = createContext<PostBotAccess>(denied)
export const usePostBotNavigationAccess = () => useContext(PostBotAccessContext)

/** Bind optional page props explicitly, and discard mounted drafts on account
 * change (not on a same-user refresh). Retained workflows keep their old gate. */
export function PostBotResource({
  page: Page,
}: {
  page: ComponentType<PostBotAccess>
}) {
  const auth = useAuth()
  const canRead = hasAccessPolicy(App.AccessPolicy.POST_BOT_READ, auth)
  const canWrite = hasAccessPolicy(App.AccessPolicy.POST_BOT_WRITE, auth)
  const access: PostBotAccess = {
    canRead,
    canWrite,
    readOnly: !canWrite,
    canAccessRetained:
      auth?.type === App.AuthType.OIDC
        ? hasModuleAccess(
            (auth.userInfo as App.OIDCUserInfo | undefined)?.roles ?? [],
            App.ModuleName.POST_BOT,
          )
        : (auth?.role ?? App.UserRole.ANON) >= App.UserRole.MANAGER,
  }
  return (
    <PostBotAccessContext.Provider
      key={postBotSessionIdentity(auth)}
      value={access}
    >
      <Page {...access} />
    </PostBotAccessContext.Provider>
  )
}
